import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import Loader from "../../../../components/template/loader.jsx";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";

const TaskActionModal = ({payload, onClose, onSuccess}) => {
    const {configuration} = useDashboardContext();
    const [mileage, setMileage] = useState("");
    const [mileageWarning, setMileageWarning] = useState(null);
    const [saveInvalidMileage, setSaveInvalidMileage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requested = Boolean(payload);
    const mode = payload?.mode ?? "start-task";
    const garrisonId = payload?.garrisonId;
    const taskId = payload?.taskId;
    const task = payload?.task;

    const taskQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonBySetDispatch(garrisonId),
        enabled: requested && Boolean(garrisonId),
        queryFn: () => DispatchService.getGarrisonBySetDispatch(garrisonId),
    });
    const isReady = !taskQuery.isFetching && (taskQuery.isSuccess || taskQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;

        setMileage("");
        setMileageWarning(null);
        setSaveInvalidMileage(false);
        setIsSubmitting(false);
    }, [requested, garrisonId, mode]);

    useEffect(() => {
        const result = taskQuery.data;
        const garrison = result?.garrison;
        if (!requested || mode !== "start-task" || !garrison?.kmControl) return;

        if (configuration.isSuggestKm && garrison.actualKm !== undefined && garrison.actualKm !== null) {
            setMileage(String(garrison.actualKm));
        }
    }, [configuration.isSuggestKm, mode, requested, taskQuery.data]);

    const result = taskQuery.data ?? {};
    const garrison = result.garrison ?? payload?.garrison;
    const dispatchToCloseId = result.dispatch?.id ?? payload?.dispatchToCloseId ?? null;
    const kmControl = Boolean(garrison?.kmControl);
    const actualKm = garrison?.actualKm ?? null;
    const hasError = taskQuery.isError;
    const isLoading = taskQuery.isLoading;

    const taskTitle = useMemo(() => mode === "assignment-task" ? "Atribuir" : "Assumir tarefa", [mode]);

    const handleMileageChange = ({target}) => {
        setMileage(target.value);
        setMileageWarning(null);
        setSaveInvalidMileage(false);
    };

    const validateMileage = async () => {
        if (mode !== "start-task" || !kmControl || mileage === "") return true;

        const mileageResult = await DispatchService.checkMileage(garrisonId, mileage);
        if (!mileageResult?.invalid || saveInvalidMileage) return true;

        setMileageWarning({
            actualKm: mileageResult.actualKm === "" || mileageResult.actualKm === null || mileageResult.actualKm === undefined ? "0" : mileageResult.actualKm,
        });
        return false;
    };

    const handleSubmit = async () => {
        if (!garrisonId || !taskId) {
            projectUtils.swalErrorMessage("Erro", "Não foi possível identificar a guarnição e a tarefa.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const mileageIsValid = await validateMileage();
            if (!mileageIsValid) {
                setIsSubmitting(false);
                return;
            }

            if (mode === "assignment-task") {
                await DispatchService.dispatchAssignment({
                    garrisonId,
                    attendanceId: null,
                    taskId,
                });

                onSuccess();
                projectUtils.swalSuccessMessage("success", "Sucesso", "Tarefa atribuída com sucesso.", "Ok");
                return;
            }

            const response = await DispatchService.dispatchGenerateByTask({
                garrisonId,
                taskId,
                dispatchToCloseId,
                actualKm: mileage,
                finalObservation: null,
            });

            if (!response || response === "BAD_REQUEST") {
                projectUtils.swalErrorMessage("Erro", "Não foi possível assumir a tarefa.", "Ok");
                setIsSubmitting(false);
                return;
            }

            const checkbook = response.numberCheckbook ? ` Talão ${response.numberCheckbook}.` : "";
            onSuccess();
            projectUtils.swalSuccessMessage("success", "Sucesso", `Tarefa assumida com sucesso.${checkbook}`, "Ok");
        } catch (error) {
            console.error("Erro ao processar tarefa pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível processar a tarefa.";
            projectUtils.swalErrorMessage("Erro", message, "Ok");
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title={taskTitle} className="w-full max-w-2xl" isCloseClick={false} animated={false}>
            <Modal.Body className="relative max-h-[calc(100vh-180px)] flex flex-col gap-4 overflow-auto">
                {hasError && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        Não foi possível carregar os dados da tarefa.
                    </div>
                )}

                {!hasError && (
                    <>
                        <div>
                            <p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p>
                            <span className="text-sm text-[#555]">{garrison?.name}</span>
                        </div>

                        <div className="flex flex-col">
                            <p className="text-[14px] font-bold text-[#676a6c]">Tarefa</p>
                            {task?.reasonText ? <span className="text-sm text-[#555]">{task.reasonText}</span> : null}
                            {task?.description ? <span className="text-sm text-[#555]">{task.description}</span> : null}
                            <span className="text-sm text-[#555]">{task?.placeText || task?.place}</span>
                        </div>

                        {mode === "start-task" && kmControl && (
                            <InputForm
                                id="dashboard-task-mileage"
                                label="Km do veículo"
                                type="number"
                                value={mileage}
                                min={1}
                                onChange={handleMileageChange}
                                integerOnly
                            />
                        )}

                        {mode === "start-task" && mileageWarning && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                <div className="mb-2 text-center">
                                    O km informado é menor que o atual. Deseja continuar?<br/>
                                    Km atual: <b>{mileageWarning.actualKm}</b>
                                </div>
                                <InputCheckbox
                                    id="dashboard-task-save-invalid-mileage"
                                    label="Sim, continuar mesmo assim"
                                    checked={saveInvalidMileage}
                                    onChange={({target}) => setSaveInvalidMileage(target.checked)}
                                />
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" target="primary" onClick={handleSubmit} loading={isSubmitting} disabled={hasError || isLoading} actionFeedbackType='save'>

                    Salvar
                </Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default TaskActionModal;